import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Document, Page } from "react-pdf";
import type { DocumentProps } from "react-pdf";
import * as pdfjsLib from "pdfjs-dist";
import type { PDFDocumentProxy } from "pdfjs-dist";
import { alpha, useTheme } from "@mui/material";

import { BaseViewerProps } from "./BaseViewer";
import ChevronDownIcon from "../icons/ChevronDownIcon";
import ChevronLeftIcon from "../icons/ChevronLeftIcon";
import ChevronRightIcon from "../icons/ChevronRightIcon";
import ChevronUpIcon from "../icons/ChevronUpIcon";
import CloseIcon from "../icons/CloseIcon";
import { SearchIcon } from "../icons/SearchIcon";
import RotateIcon from "../icons/RotateIcon";
import FitToWidthIcon from "../icons/FitToWidthIcon";
import FitWidthIcon from "../icons/FitWidthIcon";
import FitHeightIcon from "../icons/FitHeightIcon";
import ActualSizeIcon from "../icons/ActualSizeIcon";
import { CheckIcon } from "../icons/CheckIcon";

// Import CSS for react-pdf
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { IPageRotation } from "../../types/chat";

const PAGE_GAP = 16;
const ROW_HORIZONTAL_PADDING = 16; /* small margin so the scaled page shadow is never clipped */
const OVERSCAN = 2;
const DEFAULT_PAGE_HEIGHT_PT = 842;
const DEFAULT_PAGE_WIDTH_PT = 595;
/** Delays (ms) to re-apply highlights after scroll-to-match so late-mounted text layers get highlighted */
const HIGHLIGHT_REAPPLY_DELAYS = [0, 350, 700, 1200, 2000];

// Worker: pdfjs-dist (same version as react-pdf uses; worker copied in prebuild with version in filename for cache busting)
pdfjsLib.GlobalWorkerOptions.workerSrc = `/pdf.worker.${pdfjsLib.version}.js`;


interface SearchMatch {
    pageIndex: number;
    matchIndex: number;
}

type ZoomMode = "fit-width" | "fit-height" | number;

const ZOOM_OPTIONS: { key: string; label: string; value: ZoomMode; icon: React.FC<{ className?: string }> }[] = [
    { key: "fit-width", label: "Fit to width", value: "fit-width", icon: FitWidthIcon },
    { key: "fit-height", label: "Fit height", value: "fit-height", icon: FitHeightIcon },
    { key: "actual-size", label: "Actual size", value: 1, icon: ActualSizeIcon },
];

const DEFAULT_ZOOM_MODE: ZoomMode = "fit-width";
const ZOOM_STEP = 0.1;
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 3;

interface PdfViewerProps extends BaseViewerProps {
    content?: string;
    fileName?: string;
    rotation?: IPageRotation;
    // Page to scroll to
    initialPage?: number | null;
}

const normalizeDegrees = (degrees: number): number => ((degrees % 360) + 360) % 360;

/** Page index → API-reported clockwise rotation of page content (0-based). */
function toPageRotationFromApi(
    rotation?: Record<number, number> | { file_id?: string; [key: string]: number | string | undefined },
): Record<number, number> {
    if (!rotation) return {};
    const out: Record<number, number> = {};
    for (const [key, value] of Object.entries(rotation)) {
        if (key === "file_id" || typeof value !== "number") continue;
        const pageIndex = Number(key);
        if (!Number.isNaN(pageIndex)) out[pageIndex] = normalizeDegrees(value);
    }
    return out;
}

type ListImperativeAPI = {
    element: HTMLDivElement | null;
    scrollToRow: ({ index, align, behavior }: { index: number; align?: "start" | "center" | "end"; behavior?: "auto" | "smooth" }) => void;
}

/**
 * pdf.js rotate for upright display.
 * API value is how far content is rotated clockwise; PDF /Rotate is applied separately in the file.
 */
function apiRotationToDisplayRotation(
    apiDegrees: number,
    pdfIntrinsicDegrees: number,
): number {
    return normalizeDegrees(360 - apiDegrees + pdfIntrinsicDegrees);
}

const PdfViewer: React.FC<PdfViewerProps> = ({ content = "", rotation, initialPage }) => {
    const theme = useTheme();
    const pageRotationFromApi = useMemo(() => toPageRotationFromApi(rotation), [rotation]);
    const [numPages, setNumPages] = useState<number | null>(null);
    const [zoomMode, setZoomMode] = useState<ZoomMode>(DEFAULT_ZOOM_MODE);
    const [scale, setScale] = useState<number>(1.0);
    const [zoomMenuOpen, setZoomMenuOpen] = useState<boolean>(false);
    const zoomMenuRef = useRef<HTMLDivElement>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<boolean>(false);
    const [remountKey, setRemountKey] = useState<number>(0);

    const [pdfFile, setPdfFile] = useState<DocumentProps['file'] | undefined>(undefined);

    // Search state
    const [searchOpen, setSearchOpen] = useState<boolean>(false);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [searchMatches, setSearchMatches] = useState<SearchMatch[]>([]);
    const [currentMatchIndex, setCurrentMatchIndex] = useState<number>(-1);
    const [pdfDocument, setPdfDocument] = useState<PDFDocumentProxy | null>(null);
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const [pageInputValue, setPageInputValue] = useState<string>('1');
    const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());
    const containerRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<ListImperativeAPI | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    /** Previous currentMatchIndex; scroll only when it changed (next/prev or new search), not when effect re-runs due to other deps. */
    const prevMatchIndexRef = useRef<number>(-1);
    /** Match index we are scrolling to (used by delayed scroll-into-view so it always has the right index). */
    const scrollToMatchIndexRef = useRef<number>(-1);
    const [pageHeightsPx, setPageHeightsPx] = useState<number[]>([]);
    const [pdfIntrinsicRotations, setPdfIntrinsicRotations] = useState<number[]>([]);
    const [pageWidthPx, setPageWidthPx] = useState<number>(DEFAULT_PAGE_WIDTH_PT);

    const pageDisplayRotations = useMemo(() => {
        const out: Record<number, number> = {};
        for (const [indexKey, apiDegrees] of Object.entries(pageRotationFromApi)) {
            const index = Number(indexKey);
            if (Number.isNaN(index)) continue;
            out[index] = apiRotationToDisplayRotation(
                apiDegrees,
                pdfIntrinsicRotations[index] ?? 0,
            );
        }
        return out;
    }, [pageRotationFromApi, pdfIntrinsicRotations]);

    const [listHeight, setListHeight] = useState(400);
    const [listWidth, setListWidth] = useState(400);
    const [scrollTop, setScrollTop] = useState(0);

    // For blob URLs, we need to disable streaming and range requests since blobs don't support them
    const documentOptions = useMemo(() => {
        const isBlob = (content || '')?.trim?.()?.startsWith?.('blob:');
        return {
            cMapUrl: undefined,
            cMapPacked: false,
            disableStream: isBlob,  // Disable streaming for blob URLs
            disableRange: isBlob,   // Disable range requests for blob URLs
        };
    }, [content]);

    useEffect(() => {
        const s = (content || '').trim();
        let objectUrl: string | undefined;
        
        // Set loading state when we start processing
        setLoading(true);
        setError(false);
        
        if (!s) { 
            setPdfFile(undefined);
            setLoading(false);
            setError(false);
            return; 
        }

        try {
            if (s.startsWith('blob:')) {
                setPdfFile({ url: s });
                // Reset loading state when we have a valid PDF file
                setLoading(false);
                setError(false);
            } else if (s.startsWith('data:application/pdf;base64,')) {
                const cleaned = s.replace(/^data:application\/pdf;base64,/, "");
                const binaryString = atob(cleaned);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
                const blob = new Blob([bytes], { type: 'application/pdf' });
                objectUrl = URL.createObjectURL(blob);
                setPdfFile({ url: objectUrl });
            } else if (s.startsWith('JVBERi0')) {
                const binaryString = atob(s);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
                const blob = new Blob([bytes], { type: 'application/pdf' });
                objectUrl = URL.createObjectURL(blob);
                setPdfFile({ url: objectUrl });
            } else if (/^https?:\/\//i.test(s)) {
                setPdfFile({ url: s });
            } else {
                // Try to treat the content as direct PDF data
                try {
                    const binaryString = atob(s);
                    const bytes = new Uint8Array(binaryString.length);
                    for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
                    const blob = new Blob([bytes], { type: 'application/pdf' });
                    objectUrl = URL.createObjectURL(blob);
                    setPdfFile({ url: objectUrl });
                } catch (directErr) {
                    setPdfFile(undefined);
                    setLoading(false);
                    setError(true);
                }
            }
        } catch (err) {
            setPdfFile(undefined);
            setLoading(false);
            setError(true);
        }
        
        return () => { 
            if (objectUrl) URL.revokeObjectURL(objectUrl); 
        };
    }, [content]);

    // Debug pdfFile changes
    useEffect(() => {
        if (!loading && !error && pdfFile) {
        }
    }, [pdfFile, loading, error]);

    const onDocumentLoadSuccess = useCallback((pdf: PDFDocumentProxy) => {
        setNumPages(pdf.numPages);
        setPdfDocument(pdf);
        setPageHeightsPx([]);
        setLoading(false);
        setError(false);
        setSearchOpen(false);
        setSearchQuery('');
        setSearchMatches([]);
        setCurrentMatchIndex(-1);
        setZoomMode(DEFAULT_ZOOM_MODE);
        setPageInputValue('1');
        prevMatchIndexRef.current = -1;
        scrollToMatchIndexRef.current = -1;
    }, []);

    useEffect(() => {
        if (!pdfFile) setPdfDocument(null);
    }, [pdfFile]);

    // Measure list container size for react-window and horizontal scroll
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const ro = new ResizeObserver((entries) => {
            const { height, width } = entries[0]?.contentRect ?? { height: 400, width: 400 };
            setListHeight(Math.max(200, height));
            setListWidth(Math.max(200, width));
        });
        ro.observe(el);
        setListHeight(el.clientHeight || 400);
        setListWidth(el.clientWidth || 400);
        return () => ro.disconnect();
    }, [pdfFile]);

    // Load page dimensions in batches for VariableSizeList itemSize and content width
    useEffect(() => {
        if (!pdfDocument || !numPages) {
            setPageHeightsPx([]);
            setPdfIntrinsicRotations([]);
            return;
        }
        let cancelled = false;
        const BATCH = 25;
        const heights: number[] = new Array(numPages);
        const intrinsicRotations: number[] = new Array(numPages);
        let maxViewportWidth = 0;
        
        const loadBatch = async (from: number) => {
            const to = Math.min(from + BATCH, numPages);
            for (let i = from; i < to; i++) {
                if (cancelled) return;
                try {
                    const page = await pdfDocument.getPage(i + 1);
                    const intrinsicRotation = page.rotate ?? 0;
                    intrinsicRotations[i] = intrinsicRotation;
                    const apiDegrees = pageRotationFromApi[i];
                    const viewport =
                        apiDegrees !== undefined && apiDegrees !== null
                            ? page.getViewport({
                                scale: 1,
                                rotation: apiRotationToDisplayRotation(
                                    apiDegrees,
                                    intrinsicRotation,
                                ),
                            })
                            : page.getViewport({ scale: 1 });
                    heights[i] = viewport.height;
                    maxViewportWidth = Math.max(maxViewportWidth, viewport.width);
                } catch {
                    heights[i] = DEFAULT_PAGE_HEIGHT_PT;
                    intrinsicRotations[i] = 0;
                    maxViewportWidth = Math.max(maxViewportWidth, DEFAULT_PAGE_WIDTH_PT);
                }
            }
            if (!cancelled && to < numPages) requestAnimationFrame(() => loadBatch(to));
            else if (!cancelled) {
                setPageWidthPx(maxViewportWidth || DEFAULT_PAGE_WIDTH_PT);
                setPageHeightsPx([...heights]);
                setPdfIntrinsicRotations([...intrinsicRotations]);
            }
        };
        loadBatch(0);
        return () => { cancelled = true; };
    }, [pdfDocument, numPages, pageRotationFromApi]);

    const { totalHeight, rowTops, getRowHeight } = useMemo(() => {
        const heights: number[] = [];
        const tops: number[] = [0];
        let total = 0;
        for (let i = 0; i < (numPages ?? 0); i++) {
            const h = (pageHeightsPx[i] ?? DEFAULT_PAGE_HEIGHT_PT) * scale + PAGE_GAP;
            heights.push(h);
            total += h;
            tops.push(total);
        }
        const getRowHeight = (index: number) => heights[index] ?? DEFAULT_PAGE_HEIGHT_PT * scale + PAGE_GAP;
        return { totalHeight: total, rowTops: tops, getRowHeight };
    }, [numPages, pageHeightsPx, scale]);

    const visibleRange = useMemo(() => {
        if (!numPages || totalHeight <= 0) return { start: 0, end: 0 };
        const scrollBottom = scrollTop + listHeight;
        const start = Math.max(0, (rowTops.findIndex((t) => t > scrollTop) ?? rowTops.length) - 1);
        const rawEnd = rowTops.findIndex((t) => t >= scrollBottom) ?? numPages;
        const end = Math.min(numPages, rawEnd + OVERSCAN);
        const lastRowTop = rowTops[numPages - 1] ?? 0;
        const includeLastRow = scrollBottom > lastRowTop;
        return {
            start: Math.max(0, start - OVERSCAN),
            end: includeLastRow ? numPages : end,
        };
    }, [numPages, totalHeight, rowTops, scrollTop, listHeight]);

    /**
     * 0-based index of the page currently at the top of the viewport, used for the page indicator and "fit to page" sizing.
     * A 1px tolerance absorbs sub-pixel rounding when a smooth scroll lands just short of a page's exact top offset.
     */
    const currentPageIndex = useMemo(() => {
        if (!numPages || rowTops.length < 2) return 0;
        let idx = 0;
        for (let i = 0; i < numPages; i++) {
            if (rowTops[i] <= scrollTop + 1) idx = i;
            else break;
        }
        return Math.min(idx, numPages - 1);
    }, [numPages, rowTops, scrollTop]);

    useEffect(() => {
        setPageInputValue(String(currentPageIndex + 1));
    }, [currentPageIndex]);

    // Recompute scale whenever the zoom mode or layout metrics change; a plain number mode is used as-is.
    useEffect(() => {
        if (typeof zoomMode === "number") {
            setScale(zoomMode);
            return;
        }
        const width = pageWidthPx || DEFAULT_PAGE_WIDTH_PT;
        const widthScale = Math.max(0.2, (listWidth - ROW_HORIZONTAL_PADDING) / width);
        if (zoomMode === "fit-width") {
            setScale(widthScale);
            return;
        }
        // fit-height
        const height = pageHeightsPx[currentPageIndex] ?? DEFAULT_PAGE_HEIGHT_PT;
        const heightScale = Math.max(0.2, (listHeight - PAGE_GAP * 2) / height);
        setScale(heightScale);
    }, [zoomMode, listWidth, listHeight, pageWidthPx, pageHeightsPx, currentPageIndex]);

    // Close the zoom dropdown on outside click.
    useEffect(() => {
        if (!zoomMenuOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (zoomMenuRef.current && !zoomMenuRef.current.contains(e.target as Node)) {
                setZoomMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [zoomMenuOpen]);

    useEffect(() => {
        if (!numPages || rowTops.length < 2) return;
        const el = scrollContainerRef.current;
        listRef.current = {
            element: el,
            scrollToRow: ({ index, align = "start", behavior = "auto" }) => {
                const scrollEl = scrollContainerRef.current;
                if (!scrollEl) return;
                const rowTop = rowTops[index] ?? 0;
                const rowH = getRowHeight(index);
                const containerH = scrollEl.clientHeight;
                let targetTop = rowTop;
                if (align === "center") targetTop = rowTop - containerH / 2 + rowH / 2;
                else if (align === "end") targetTop = rowTop - containerH + rowH;
                targetTop = Math.max(0, Math.min(targetTop, scrollEl.scrollHeight - containerH));
                scrollEl.scrollTo({ top: targetTop, behavior: behavior === "smooth" ? "smooth" : "auto" });
            },
        };
        return () => {
            listRef.current = null;
        };
    }, [numPages, rowTops, getRowHeight]);

    // Scroll to the requested page once the document (and its row layout) is ready.
    // Keyed on content+initialPage so it fires once per document/citation, not on every rowTops recompute.
    const appliedInitialPageRef = useRef<string | null>(null);
    useEffect(() => {
        if (!initialPage || !numPages || !listRef.current) {
            return;
        }

        const key = `${content}|${initialPage}`;
        if (appliedInitialPageRef.current === key) {
            return;
        }

        appliedInitialPageRef.current = key;
        const targetIndex = Math.max(0, Math.min(numPages - 1, initialPage - 1));
        
        listRef.current.scrollToRow({ index: targetIndex, align: "start", behavior: "auto" });
    }, [initialPage, numPages, rowTops, content]);

    // Search through PDF text content
    const searchAbortRef = useRef<boolean>(false);

    const performSearch = useCallback(async (query: string) => {
        if (!pdfDocument || !query.trim()) {
            setSearchMatches([]);
            setCurrentMatchIndex(-1);
            return;
        }

        searchAbortRef.current = false;
        setIsSearching(true);
        const searchText = query.toLowerCase().replace(/\s+/g, "");
        const BATCH_SIZE = 25;

        const yieldToMain = () => new Promise<void>((r) => setTimeout(r, 0));

        try {
            const totalPages = pdfDocument.numPages;
            const allMatches: SearchMatch[] = [];

            for (let from = 1; from <= totalPages; from += BATCH_SIZE) {
                if (searchAbortRef.current) return;
                const to = Math.min(from + BATCH_SIZE, totalPages + 1);

                for (let pageNum = from; pageNum < to; pageNum++) {
                    if (searchAbortRef.current) return;
                    const page = await pdfDocument.getPage(pageNum);
                    const textContent = await page.getTextContent();
                    const pageText = textContent.items
                        .map((item: any) => ("str" in item ? item.str : ""))
                        .join("")
                        .replace(/\s+/g, " ")
                        .toLowerCase();
                    const pageTextNoSpaces = pageText.replace(/\s+/g, "");

                    let matchIndex = 0;
                    let startIndex = 0;
                    while ((startIndex = pageTextNoSpaces.indexOf(searchText, startIndex)) !== -1) {
                        allMatches.push({ pageIndex: pageNum, matchIndex });
                        matchIndex++;
                        startIndex += searchText.length;
                    }
                }

                setSearchMatches([...allMatches]);
                await yieldToMain();
            }

            setSearchMatches(allMatches);
            // Only set currentMatchIndex when search is fully complete; otherwise batch updates would reset it and scroll would jump back to the first match.
            setCurrentMatchIndex(allMatches.length > 0 ? 0 : -1);
        } catch (err) {
            if (!searchAbortRef.current) console.error("Search error:", err);
        } finally {
            setIsSearching(false);
        }
    }, [pdfDocument]);
    
    // Debounced search (abort previous when query changes)
    useEffect(() => {
        searchAbortRef.current = true;
        const timer = setTimeout(() => {
            performSearch(searchQuery);
        }, 300);
        return () => {
            searchAbortRef.current = true;
            clearTimeout(timer);
        };
    }, [searchQuery, performSearch]);
    
    // Scroll to current match (highlights re-applied below after getPageNumberFromTextLayer exists)
    
    // Helper function to map normalized text index to original text index
    const normalizedToOriginalIndex = useCallback((normalizedIdx: number, original: string): number => {
        let normalizedCount = 0;
        for (let i = 0; i < original.length; i++) {
            if (!/\s/.test(original[i])) {
                if (normalizedCount === normalizedIdx) {
                    return i;
                }
                normalizedCount++;
            }
        }
        return original.length;
    }, []);
    
    // Helper function to get the bounding rect of a character range using Range API
    // This provides pixel-accurate positioning regardless of font/character widths
    const getCharacterRangeRect = useCallback((element: HTMLElement, startChar: number, endChar: number): DOMRect | null => {
        const textNode = element.firstChild;
        if (!textNode || textNode.nodeType !== Node.TEXT_NODE) return null;
        
        const textLength = textNode.textContent?.length || 0;
        // Clamp values to valid range
        const safeStart = Math.max(0, Math.min(startChar, textLength));
        const safeEnd = Math.max(safeStart, Math.min(endChar, textLength));
        
        if (safeStart >= safeEnd) return null;
        
        try {
            const range = document.createRange();
            range.setStart(textNode, safeStart);
            range.setEnd(textNode, safeEnd);
            return range.getBoundingClientRect();
        } catch {
            return null;
        }
    }, []);
    
    // Function to apply highlighting to a single text layer
    // pageNum is the 1-based page number, globalMatches is the searchMatches array for calculating global indices
    const applyHighlightToTextLayer = useCallback((textLayer: Element, query: string, pageNum: number, globalMatches: SearchMatch[]) => {
        const spans = Array.from(textLayer.querySelectorAll('span:not(.pdf-search-highlight)'));
        
        // Remove previous highlight overlays
        textLayer.querySelectorAll('.pdf-search-highlight').forEach(el => el.remove());
        
        if (!query) return;
        
        // Build cumulative text to find where matches occur
        // Track both normalized (no spaces) and original text for each span
        let cumulativeText = '';
        const spanRanges: { 
            start: number; 
            end: number; 
            span: Element;
            originalText: string;
            normalizedText: string;
        }[] = [];
        
        spans.forEach(span => {
            const originalText = (span.textContent || '').toLowerCase();
            const normalizedText = originalText.replace(/\s+/g, '');
            const start = cumulativeText.length;
            cumulativeText += normalizedText;
            spanRanges.push({ 
                start, 
                end: cumulativeText.length, 
                span, 
                originalText,
                normalizedText 
            });
        });
        
        // Find all match positions in cumulative text
        const matchPositions: { start: number; end: number; localIndex: number }[] = [];
        let searchStart = 0;
        let localMatchIndex = 0;
        while ((searchStart = cumulativeText.indexOf(query, searchStart)) !== -1) {
            matchPositions.push({ start: searchStart, end: searchStart + query.length, localIndex: localMatchIndex });
            localMatchIndex++;
            searchStart += query.length;
        }
        
        const parentRect = textLayer.getBoundingClientRect();
        
        // Helper to find global match index from page number and local match index
        const getGlobalMatchIndex = (pageIndex: number, matchIndex: number): number => {
            return globalMatches.findIndex(m => m.pageIndex === pageIndex && m.matchIndex === matchIndex);
        };
        
        // Create highlight overlays only for the exact matching portion within each span
        matchPositions.forEach(match => {
            const globalMatchIdx = getGlobalMatchIndex(pageNum, match.localIndex);
            let isFirstSpanForMatch = true; // Only add data-match-index to first span of each match
            
            spanRanges.forEach(({ start, end, span, originalText, normalizedText }) => {
                // Check if this span overlaps with the match
                if (start < match.end && end > match.start) {
                    const htmlSpan = span as HTMLElement;
                    
                    // Calculate which portion of THIS span's normalized text is part of the match
                    const spanMatchStartNorm = Math.max(0, match.start - start);
                    const spanMatchEndNorm = Math.min(normalizedText.length, match.end - start);
                    
                    if (spanMatchStartNorm >= spanMatchEndNorm) return;
                    
                    // Map normalized positions back to original positions (accounting for spaces)
                    const originalMatchStart = normalizedToOriginalIndex(spanMatchStartNorm, originalText);
                    const originalMatchEnd = normalizedToOriginalIndex(spanMatchEndNorm, originalText);
                    
                    // Use Range API to get pixel-accurate bounding rect for the matched characters
                    const charRect = getCharacterRangeRect(htmlSpan, originalMatchStart, originalMatchEnd);
                    
                    if (charRect && charRect.width > 0) {
                        // Create highlight overlay element using exact pixel coordinates
                        const highlight = document.createElement('span');
                        highlight.className = 'pdf-search-highlight';
                        // Add data-match-index only to the first span of each match (for scrolling)
                        if (isFirstSpanForMatch && globalMatchIdx >= 0) {
                            highlight.setAttribute('data-match-index', String(globalMatchIdx));
                            isFirstSpanForMatch = false;
                        }
                        highlight.style.cssText = `
                            position: absolute;
                            left: ${charRect.left - parentRect.left}px;
                            top: ${charRect.top - parentRect.top}px;
                            width: ${charRect.width}px;
                            height: ${charRect.height}px;
                            background-color: ${alpha(theme.palette.info.light, 0.3)};
                            border: 2px solid ${theme.palette.info.light};
                            pointer-events: none;
                            border-radius: 2px;
                        `;
                        textLayer.appendChild(highlight);
                    } else {
                        // Fallback to proportional calculation if Range API fails
                        const rect = htmlSpan.getBoundingClientRect();
                        const originalLength = originalText.length;
                        if (originalLength === 0) return;
                        
                        const startRatio = originalMatchStart / originalLength;
                        const endRatio = originalMatchEnd / originalLength;
                        
                        const highlightLeft = rect.left - parentRect.left + (rect.width * startRatio);
                        const highlightWidth = rect.width * (endRatio - startRatio);
                        
                        const highlight = document.createElement('span');
                        highlight.className = 'pdf-search-highlight';
                        // Add data-match-index only to the first span of each match (for scrolling)
                        if (isFirstSpanForMatch && globalMatchIdx >= 0) {
                            highlight.setAttribute('data-match-index', String(globalMatchIdx));
                            isFirstSpanForMatch = false;
                        }
                        highlight.style.cssText = `
                            position: absolute;
                            left: ${highlightLeft}px;
                            top: ${rect.top - parentRect.top}px;
                            width: ${highlightWidth}px;
                            height: ${rect.height}px;
                            background-color: ${alpha(theme.palette.info.light, 0.3)};
                            border: 2px solid ${theme.palette.info.light};
                            pointer-events: none;
                            border-radius: 2px;
                        `;
                        textLayer.appendChild(highlight);
                    }
                }
            });
        });
    }, [normalizedToOriginalIndex, getCharacterRangeRect, theme.palette.info.light]);
    
    // Helper to extract page number from a text layer element
    const getPageNumberFromTextLayer = useCallback((textLayer: Element): number => {
        const pageElement = textLayer.closest('.react-pdf__Page');
        if (pageElement) {
            const pageNum = pageElement.getAttribute('data-page-number');
            if (pageNum) return parseInt(pageNum, 10);
        }
        return 1;
    }, []);

    // Style the current match's highlight (orange then yellow). Call after any code that (re)creates highlights so the current match stays visually selected.
    const styleCurrentMatchHighlight = useCallback(() => {
        if (currentMatchIndex < 0 || !containerRef.current) return;
        const highlightEl = containerRef.current.querySelector(
            `.pdf-search-highlight[data-match-index="${currentMatchIndex}"]`
        ) as HTMLElement;
        if (highlightEl) {
            highlightEl.style.backgroundColor = "rgba(255, 152, 0, 0.7)";
            setTimeout(() => {
                highlightEl.style.backgroundColor = "rgba(255, 235, 59, 0.5)";
            }, 1000);
        }
    }, [currentMatchIndex]);

    // Re-apply highlights to all current text layers (catches late-mounted layers on virtualized pages)
    const reapplyHighlights = useCallback(() => {
        const query = searchQuery.trim().toLowerCase().replace(/\s+/g, "");
        if (!query || !containerRef.current) return;
        containerRef.current.querySelectorAll(".react-pdf__Page__textContent").forEach((textLayer) => {
            const pageNum = getPageNumberFromTextLayer(textLayer);
            applyHighlightToTextLayer(textLayer, query, pageNum, searchMatches);
        });
        styleCurrentMatchHighlight();
    }, [
        searchQuery,
        searchMatches,
        applyHighlightToTextLayer,
        getPageNumberFromTextLayer,
        styleCurrentMatchHighlight,
    ]);

    // Scroll the current match's highlight into view by setting the scroll container's scrollTop.
    // Queries inside the scroll container so we only find highlights on mounted (visible) pages.
    const scrollCurrentMatchIntoView = useCallback(() => {
        const matchIndex = scrollToMatchIndexRef.current;
        if (matchIndex < 0) return;
        const scrollEl = scrollContainerRef.current;
        if (!scrollEl) return;
        const highlightEl = scrollEl.querySelector(
            `.pdf-search-highlight[data-match-index="${matchIndex}"]`
        ) as HTMLElement;
        if (!highlightEl) return;
        const highlightRect = highlightEl.getBoundingClientRect();
        const scrollRect = scrollEl.getBoundingClientRect();
        const highlightTopInContent = scrollEl.scrollTop + (highlightRect.top - scrollRect.top);
        const targetScrollTop =
            highlightTopInContent - scrollEl.clientHeight / 2 + highlightRect.height / 2;
        const clamped = Math.max(
            0,
            Math.min(targetScrollTop, scrollEl.scrollHeight - scrollEl.clientHeight)
        );
        scrollEl.scrollTo({ top: clamped, behavior: "smooth" });
    }, []);

    // Scroll to current match only when the match index changed (next/prev or new search), not when effect re-runs due to other deps.
    // Re-apply highlights when the target page mounts (virtualized). When navigating, run scroll-into-view after each reapply so the highlight exists.
    useEffect(() => {
        if (currentMatchIndex < 0 || !searchMatches[currentMatchIndex]) {
            prevMatchIndexRef.current = currentMatchIndex;
            scrollToMatchIndexRef.current = -1;
            return;
        }
        const match = searchMatches[currentMatchIndex];
        const pageIndex = match.pageIndex - 1;
        const matchIndexChanged = prevMatchIndexRef.current !== currentMatchIndex;
        if (matchIndexChanged && listRef.current) {
            scrollToMatchIndexRef.current = currentMatchIndex;
            const prevMatch =
                prevMatchIndexRef.current >= 0 ? searchMatches[prevMatchIndexRef.current] : undefined;
            const samePage = prevMatch && prevMatch.pageIndex === match.pageIndex;
            if (!samePage) {
                listRef.current.scrollToRow({ index: pageIndex, align: "center", behavior: "smooth" });
            }
        }
        prevMatchIndexRef.current = currentMatchIndex;
        const scrollIntoViewRafIds: number[] = [];
        const timeouts: ReturnType<typeof setTimeout>[] = [];
        const doScrollIntoView = matchIndexChanged;
        HIGHLIGHT_REAPPLY_DELAYS.forEach((delay) => {
            const run = () => {
                reapplyHighlights();
                if (doScrollIntoView) {
                    requestAnimationFrame(() => {
                        scrollIntoViewRafIds.push(
                            requestAnimationFrame(() => scrollCurrentMatchIntoView())
                        );
                    });
                }
            };
            if (delay === 0) {
                // When navigating, yield so scroll event and re-render can run before we look for the highlight.
                if (doScrollIntoView) {
                    timeouts.push(setTimeout(run, 0));
                } else {
                    run();
                }
            } else {
                timeouts.push(setTimeout(run, delay));
            }
        });
        return () => {
            timeouts.forEach((t) => clearTimeout(t));
            scrollIntoViewRafIds.forEach((id) => cancelAnimationFrame(id));
        };
    }, [currentMatchIndex, searchMatches, reapplyHighlights, scrollCurrentMatchIntoView]);

    // Apply highlighting to text layer with MutationObserver for dynamic content
    useEffect(() => {
        const query = searchQuery.trim().toLowerCase().replace(/\s+/g, '');
        
        // Clear all previous highlight overlays first
        document.querySelectorAll('.pdf-search-highlight').forEach(el => el.remove());
        
        if (!query || !containerRef.current) {
            return;
        }
        
        // Apply highlights to existing text layers
        const textLayers = document.querySelectorAll('.react-pdf__Page__textContent');
        textLayers.forEach((textLayer) => {
            const pageNum = getPageNumberFromTextLayer(textLayer);
            applyHighlightToTextLayer(textLayer, query, pageNum, searchMatches);
        });
        styleCurrentMatchHighlight();
        
        // Watch for new spans being added (text layer renders async)
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                    // Skip if only highlight elements were added (prevents infinite loop)
                    const nonHighlightNodes = Array.from(mutation.addedNodes).filter(
                        node => !(node instanceof Element && node.classList?.contains('pdf-search-highlight'))
                    );
                    if (nonHighlightNodes.length === 0) return;
                    
                    // Check if spans were added to a text layer
                    const target = mutation.target as Element;
                    if (target.classList?.contains('react-pdf__Page__textContent')) {
                        const pageNum = getPageNumberFromTextLayer(target);
                        applyHighlightToTextLayer(target, query, pageNum, searchMatches);
                    }
                    // Also check if a text layer was added
                    nonHighlightNodes.forEach((node) => {
                        if (node instanceof Element) {
                            if (node.classList?.contains('react-pdf__Page__textContent')) {
                                const pageNum = getPageNumberFromTextLayer(node);
                                applyHighlightToTextLayer(node, query, pageNum, searchMatches);
                            }
                            // Check descendants
                            node.querySelectorAll('.react-pdf__Page__textContent').forEach((textLayer) => {
                                const pageNum = getPageNumberFromTextLayer(textLayer);
                                applyHighlightToTextLayer(textLayer, query, pageNum, searchMatches);
                            });
                        }
                    });
                    styleCurrentMatchHighlight();
                }
            });
        });
        
        observer.observe(containerRef.current, {
            childList: true,
            subtree: true,
        });
        
        return () => {
            observer.disconnect();
        };
    }, [searchQuery, numPages, loading, searchMatches, applyHighlightToTextLayer, getPageNumberFromTextLayer, styleCurrentMatchHighlight]);
    
    const goToNextMatch = () => {
        if (searchMatches.length > 0) {
            setCurrentMatchIndex(prev => (prev + 1) % searchMatches.length);
        }
    };
    
    const goToPrevMatch = () => {
        if (searchMatches.length > 0) {
            setCurrentMatchIndex(prev => (prev - 1 + searchMatches.length) % searchMatches.length);
        }
    };
    
    const closeSearch = () => {
        setSearchOpen(false);
        setSearchQuery('');
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            if (e.shiftKey) {
                goToPrevMatch();
            } else {
                goToNextMatch();
            }
        } else if (e.key === 'Escape') {
            closeSearch();
        }
    };

    const onDocumentLoadError = (e: any) => {
        
        // Retry once by forcing remount (helps if worker loads slowly)
        if (!error) {
            setLoading(true);
            setError(true);
            setRemountKey((k) => k + 1);
            return;
        }
        setLoading(false);
        setError(true);
    };


    const resetZoom = () => {
        setZoomMode(DEFAULT_ZOOM_MODE);
    };

    const zoomLabel = useMemo(() => {
        if (zoomMode === "fit-width") return "Fit to width";
        if (zoomMode === "fit-height") return "Fit height";
        return `${Math.round(scale * 100)}%`;
    }, [zoomMode, scale]);

    const isZoomOptionActive = (option: ZoomMode) =>
        typeof option === "number" ? zoomMode === option : zoomMode === option;

    const selectZoomOption = (option: ZoomMode) => {
        setZoomMode(option);
        setZoomMenuOpen(false);
    };

    const zoomOut = () => {
        setZoomMode(Math.max(MIN_ZOOM, Math.round((scale - ZOOM_STEP) * 100) / 100));
    };

    const zoomIn = () => {
        setZoomMode(Math.min(MAX_ZOOM, Math.round((scale + ZOOM_STEP) * 100) / 100));
    };

    const goToPage = (pageIndex: number) => {
        if (!numPages) return;
        const clamped = Math.max(0, Math.min(numPages - 1, pageIndex));
        listRef.current?.scrollToRow({ index: clamped, align: "start", behavior: "smooth" });
    };

    const goToPrevPage = () => goToPage(currentPageIndex - 1);
    const goToNextPage = () => goToPage(currentPageIndex + 1);

    const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const digitsOnly = e.target.value.replace(/[^0-9]/g, '');
        
        if (numPages && digitsOnly && parseInt(digitsOnly, 10) > numPages) {
            setPageInputValue(String(numPages));
            return;
        }

        setPageInputValue(digitsOnly);
    };

    const commitPageInput = () => {
        const parsed = parseInt(pageInputValue, 10);
        if (!numPages || Number.isNaN(parsed)) {
            setPageInputValue(String(currentPageIndex + 1));
            return;
        }
        goToPage(parsed - 1);
    };

    const handlePageInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.currentTarget.blur();
        }
    };

    const contentWidth = Math.max(
        listWidth,
        (pageWidthPx || DEFAULT_PAGE_WIDTH_PT) * scale + ROW_HORIZONTAL_PADDING
    );
    const needsHorizontalScroll = contentWidth > listWidth;

    if (loading && !pdfFile) {
        return (
            <div className="w-full h-height-pdf min-h-height-pdf-min">
                <div className="w-full h-full border-2 border-dashed border-hex-gray-300 rounded-lg flex items-center justify-center">
                    <div className="text-center">
                        <div className="text-2xl text-hex-gray-400 mb-2">📄</div>
                        <div className="text-sm text-hex-gray-500">Loading PDF...</div>
                    </div>
                </div>
            </div>
        );
    }

    if (!pdfFile) {
        return (
            <div className="w-full h-height-pdf min-h-height-pdf-min">
                <div className="w-full h-full border-2 border-dashed border-hex-gray-300 rounded-lg flex items-center justify-center">
                    <div className="text-center">
                        <div className="text-6xl text-hex-gray-400 mb-4">❌</div>
                        <div className="text-lg text-hex-gray-600 mb-2">Unable to load PDF</div>
                        <div className="text-sm text-hex-gray-500">The PDF content could not be processed</div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="w-full h-height-pdf min-h-height-pdf-min">
                <div className="w-full h-full border-2 border-dashed border-hex-gray-300 rounded-lg flex items-center justify-center">
                    <div className="text-center">
                        <div className="text-6xl text-hex-gray-400 mb-4">⚠️</div>
                        <div className="text-lg text-hex-gray-600 mb-2">Error loading PDF</div>
                        <div className="text-sm text-hex-gray-500">There was an issue rendering the PDF document</div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full h-full min-h-0 flex flex-col">
            {/* PDF Controls */}
            <div className="flex items-center justify-start p-0 w-full">
                <div className="flex flex-wrap items-center gap-1.5 rounded-[12px] bg-hex-gray-200 p-1.5">
                    {/* Zoom dropdown */}
                    <div className="relative" ref={zoomMenuRef}>
                        <button
                            type="button"
                            onClick={() => setZoomMenuOpen((open) => !open)}
                            className="flex items-center gap-1.5 rounded-[8px] bg-hex-white px-3 py-1.5 text-sm text-hex-gray-700 shadow-xs hover:bg-hex-gray-50 h-[32px]"
                            title="Zoom level"
                        >
                            <FitToWidthIcon className="w-4 h-4 text-hex-gray-500" />
                            <span className="whitespace-nowrap">{zoomLabel}</span>
                            <ChevronDownIcon className="w-2.5 h-1.5 text-hex-gray-400" />
                        </button>
                        {zoomMenuOpen && (
                            <div className="absolute z-20 top-full left-0 mt-1 w-[180px] rounded-[8px] border border-hex-gray-200 bg-hex-white py-1.5 shadow-lg">
                                {ZOOM_OPTIONS.map((option) => (
                                    <button
                                        key={option.key}
                                        type="button"
                                        onClick={() => selectZoomOption(option.value)}
                                        className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-hex-gray-700 hover:bg-hex-gray-50"
                                    >
                                        <option.icon className="w-6 text-hex-gray-500 shrink-0" />
                                        <span className="flex-1 whitespace-nowrap">{option.label}</span>
                                        {isZoomOptionActive(option.value) && (
                                            <CheckIcon size={16} className="text-hex-gray-700 shrink-0" />
                                        )}
                                    </button>
                                ))}
                                <div className="my-1.5 border-t border-hex-gray-200" />
                                <div className="flex items-center gap-2 px-3 py-1">
                                    <button
                                        type="button"
                                        onClick={zoomOut}
                                        disabled={scale <= MIN_ZOOM}
                                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] border border-hex-gray-300 text-base text-hex-gray-600 hover:bg-hex-gray-50 disabled:opacity-30"
                                        title="Zoom out"
                                        aria-label="Zoom out"
                                    >
                                        −
                                    </button>
                                    <span className="flex-1 rounded-[8px] border border-hex-gray-300 py-1.5 text-center text-sm">
                                        {Math.round(scale * 100)}%
                                    </span>
                                    <button
                                        type="button"
                                        onClick={zoomIn}
                                        disabled={scale >= MAX_ZOOM}
                                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] border border-hex-gray-300 text-base text-hex-gray-600 hover:bg-hex-gray-50 disabled:opacity-30"
                                        title="Zoom in"
                                        aria-label="Zoom in"
                                    >
                                        +
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Search */}
                    {!searchOpen ? (
                        <button
                            type="button"
                            onClick={() => setSearchOpen(true)}
                            className="flex items-center justify-center rounded-[8px] bg-hex-white p-2.5 shadow-xs hover:bg-hex-gray-50 h-[32px] w-[32px]"
                            title="Search in document"
                            aria-label="Search in document"
                        >
                            <SearchIcon className="w-4 h-4" />
                        </button>
                    ) : (
                        <div className="flex items-center h-[32px] gap-1 rounded-[8px] bg-hex-white pl-3 pr-1.5 py-1 shadow-xs" id="search-controls">
                            <input
                                autoFocus
                                type="text"
                                placeholder="Search in document..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={handleSearchKeyDown}
                                className="w-36 bg-transparent text-sm text-hex-gray-700 placeholder:text-hex-gray-400 focus:outline-hidden"
                            />
                            {searchQuery && (
                                <span className="whitespace-nowrap text-xs text-hex-gray-400">
                                    {isSearching ? '...' : searchMatches.length > 0
                                        ? `${currentMatchIndex + 1}/${searchMatches.length}`
                                        : '0/0'}
                                </span>
                            )}
                            <button
                                type="button"
                                onClick={goToPrevMatch}
                                disabled={searchMatches.length === 0}
                                className="flex items-center justify-center rounded-[8px] h-[32px] w-[32px] p-1 hover:bg-hex-gray-100 disabled:opacity-30"
                                title="Previous match (Shift+Enter)"
                            >
                                <ChevronUpIcon className="w-3 h-3" />
                            </button>
                            <button
                                type="button"
                                onClick={goToNextMatch}
                                disabled={searchMatches.length === 0}
                                className="flex items-center justify-center rounded-[8px] h-[32px] w-[32px] p-1 hover:bg-hex-gray-100 disabled:opacity-30"
                                title="Next match (Enter)"
                            >
                                <ChevronDownIcon className="w-3 h-[12px]" />
                            </button>
                            <button
                                type="button"
                                onClick={closeSearch}
                                className="flex items-center justify-center rounded-[8px] h-[32px] w-[32px] p-1 hover:bg-hex-gray-100"
                                title="Close search (Esc)"
                            >
                                <CloseIcon size={14} />
                            </button>
                        </div>
                    )}

                    {/* Page navigation */}
                    <div className="flex items-center gap-1.5">
                        <button
                            type="button"
                            onClick={goToPrevPage}
                            disabled={!numPages || currentPageIndex <= 0}
                            className="flex items-center justify-center rounded-[8px] h-[32px] w-[32px] text-hex-gray-500 disabled:opacity-30 bg-hex-white shadow-xs hover:bg-hex-gray-50"
                            title="Previous page"
                            aria-label="Previous page"
                        >
                            <ChevronLeftIcon />
                        </button>
                        <div className="flex items-center bg-hex-white px-2.5 py-1.5 shadow-xs h-[32px] rounded-[8px]">
                            <input
                                type="text"
                                inputMode="numeric"
                                value={pageInputValue}
                                onChange={handlePageInputChange}
                                onKeyDown={handlePageInputKeyDown}
                                onBlur={commitPageInput}
                                disabled={!numPages}
                                className="w-5 bg-transparent text-center text-sm text-hex-gray-700 focus:outline-hidden disabled:opacity-50"
                            />
                        </div>
                        <span className="px-1 text-sm whitespace-nowrap">
                            / {numPages ?? '-'}
                        </span>
                        <button
                            type="button"
                            onClick={goToNextPage}
                            disabled={!numPages || currentPageIndex >= numPages - 1}
                            className="flex items-center justify-center rounded-[8px] h-[32px] w-[32px] text-hex-gray-700 disabled:opacity-30 bg-hex-white shadow-xs hover:bg-hex-gray-50"
                            title="Next page"
                            aria-label="Next page"
                        >
                            <ChevronRightIcon />
                        </button>
                    </div>

                    {/* Reset zoom */}
                    <button
                        type="button"
                        onClick={resetZoom}
                        className="flex items-center justify-center rounded-[8px] h-[32px] w-[32px] bg-hex-white shadow-xs hover:bg-hex-gray-50"
                        title="Reset zoom"
                        aria-label="Reset zoom"
                    >
                        <RotateIcon />
                    </button>
                </div>
            </div>

            {/* PDF Content - single scroll container is the inner div below */}
            <div className="flex-1 min-h-0 flex flex-col min-w-0" ref={containerRef}>
                {loading && !pdfFile && (
                    <div className="flex items-center justify-center flex-1">
                        <div className="text-center">
                            <div className="text-2xl text-hex-gray-400 mb-2">📄</div>
                            <div className="text-sm text-hex-gray-500">Loading PDF...</div>
                        </div>
                    </div>
                )}
                {error && (
                    <div className="flex items-center justify-center flex-1">
                        <div className="text-center">
                            <div className="text-2xl text-red-400 mb-2">❌</div>
                            <div className="text-sm text-red-500 mb-2">Unable to load PDF</div>
                            <div className="text-xs text-gray-500">The PDF content could not be processed</div>
                        </div>
                    </div>
                )}
                {!error && pdfFile && (
                    <Document
                        key={remountKey}
                        file={pdfFile}
                        onLoadSuccess={onDocumentLoadSuccess}
                        onLoadError={onDocumentLoadError}
                        loading={null}
                        options={documentOptions}
                    >
                        {numPages ? (
                            <div
                                ref={scrollContainerRef}
                                style={{
                                    minWidth: 0,
                                    height: listHeight,
                                    overflowY: "auto",
                                    overflowX: needsHorizontalScroll ? "auto" : "hidden",
                                }}
                                onScroll={(e) => {
                                    const el = e.currentTarget;
                                    setScrollTop(el.scrollTop);
                                    requestAnimationFrame(() => {
                                        if (searchQuery.trim()) reapplyHighlights();
                                    });
                                }}
                            >
                                <div
                                    style={{
                                        width: contentWidth,
                                        minWidth: "100%",
                                        height: totalHeight,
                                        position: "relative",
                                    }}
                                >
                                    {Array.from(
                                        { length: visibleRange.end - visibleRange.start },
                                        (_, i) => {
                                            const index = visibleRange.start + i;
                                            const pageNum = index + 1;
                                            const pageRotate = pageDisplayRotations[index];
                                            const top = rowTops[index] ?? 0;
                                            const rowH = getRowHeight(index);
                                            return (
                                                <div
                                                    key={pageNum}
                                                    ref={(el) => {
                                                        if (el) pageRefs.current.set(pageNum, el);
                                                    }}
                                                    style={{
                                                        position: "absolute",
                                                        left: 0,
                                                        top,
                                                        width: contentWidth,
                                                        height: rowH,
                                                        overflow: "visible",
                                                    }}
                                                    className="flex flex-col items-center"
                                                >
                                                    <div className="relative inline-block shadow-lg">
                                                        <Page
                                                            key={`${pageNum}-${pageRotate ?? "default"}`}
                                                            pageNumber={pageNum}
                                                            scale={scale}
                                                            {...(pageRotate != null ? { rotate: pageRotate } : {})}
                                                            renderTextLayer={true}
                                                            renderAnnotationLayer={true}
                                                            className="block"
                                                        />
                                                        <div
                                                            className="pointer-events-none absolute bottom-2 left-1/2 z-10 -translate-x-1/2 border-0 bg-transparent text-center text-xs text-hex-gray-500"
                                                            id="pdf-page-number"
                                                        >
                                                            Page {pageNum} of {numPages}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        }
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center justify-center flex-1" style={{ minHeight: listHeight }}>
                                <div className="text-center">
                                    <div className="text-2xl text-hex-gray-400 mb-2">📄</div>
                                    <div className="text-sm text-hex-gray-500">Loading document…</div>
                                </div>
                            </div>
                        )}
                    </Document>
                )}
                {!loading && !error && !pdfFile && (
                    <div className="flex items-center justify-center flex-1">
                        <div className="text-center">
                            <div className="text-2xl text-hex-gray-400 mb-2">📄</div>
                            <div className="text-sm text-hex-gray-500">No PDF file to display</div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PdfViewer;


